'use client';

import { useTranslations } from 'next-intl';
import { Check } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { TemplateFieldsInfo } from '@/features/ocr-templates';

import type { PageTemplateSuggestion } from '../types';

interface OcrPageSuggestionListProps {
  suggestions: PageTemplateSuggestion[];
  value: number | null;
  onSelect: (templateId: number) => void;
  /** How many ranked suggestions to show. */
  limit?: number;
}

/**
 * Ranked template suggestions for one page, best first. Each shows its match
 * score and, for match rankings, how many of the template's fields the page
 * prints — the signal that separates a real fit from a near-miss.
 *
 * The fields info icon sits BESIDE the select button, not inside it: it is
 * its own interactive trigger, and nesting it would both be invalid markup
 * and select the template whenever the operator only meant to peek at it.
 */
export function OcrPageSuggestionList({
  suggestions,
  value,
  onSelect,
  limit = 3
}: OcrPageSuggestionListProps) {
  const t = useTranslations('ocr');

  return (
    <div className='flex w-full flex-col gap-1'>
      {suggestions.slice(0, limit).map((s, index) => {
        const selected = s.template_id === value;
        return (
          <div key={s.template_id} className='flex items-center gap-1'>
            <Button
              type='button'
              variant={selected ? 'secondary' : 'ghost'}
              size='sm'
              className='h-auto min-w-0 flex-1 items-start justify-start gap-1.5 py-1.5 text-start whitespace-normal'
              onClick={() => onSelect(s.template_id)}
              aria-pressed={selected}
            >
              {selected ? (
                <Check className='mt-0.5 h-3.5 w-3.5 shrink-0' />
              ) : (
                <span className='w-3.5 shrink-0' />
              )}
              {/* Name on its own line, never clipped — template names are long
                  and the preview panel is narrow. Signals go underneath. */}
              <span className='flex min-w-0 flex-col items-start gap-0.5'>
                <span className='break-words'>{s.name}</span>
                <span className='text-muted-foreground flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs font-normal'>
                  {index === 0 ? (
                    <Badge variant='outline' className='px-1 py-0 text-[10px]'>
                      {t('bestMatch')}
                    </Badge>
                  ) : null}
                  {s.fields_total ? (
                    <span>
                      {t('fieldsFound', {
                        found: s.fields_found ?? 0,
                        total: s.fields_total
                      })}
                    </span>
                  ) : null}
                  <span>
                    {t('matchScore', { score: Math.round(s.similarity * 100) })}
                  </span>
                </span>
              </span>
            </Button>
            <TemplateFieldsInfo templateId={s.template_id} />
          </div>
        );
      })}
    </div>
  );
}
